import {Menu} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {ServiceEventEntity} from "@ots-frontend/ots-data";
import {useCallback, useMemo} from "react";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {IconEdit, IconTrash} from "@tabler/icons-react";
import {useServiceEventAction} from "./useServiceEventAction";

const ALL_ACTIONS = [
  "EDIT",
  "DELETE",
] as const;

export type ServiceEventAction = typeof ALL_ACTIONS[number];

export type ServiceEventActionMenuDropdownProps = {
  serviceEvent: ServiceEventEntity
  actions?: ServiceEventAction[]
  onActionClick?: (action: ServiceEventAction) => void
  showEmpty?: boolean
}

export const ServiceEventActionMenuDropdown = (props: ServiceEventActionMenuDropdownProps) => {
  const {
    serviceEvent,
    actions = ALL_ACTIONS,
    onActionClick,
    showEmpty = true
  } = props

  const {hasAccess} = useAuth()
  const {t} = useTranslation(["common"])

  const visibleActions: Set<ServiceEventAction> = useMemo(() => {
    const enabledActions = new Set<ServiceEventAction>()
    const actionsSet = actions ? new Set<ServiceEventAction>(actions) : new Set<ServiceEventAction>()

    if (hasAccess("serviceevent_edit")) {
      if (actionsSet.has("EDIT")) {
        enabledActions.add("EDIT")
      }
    }

    if (hasAccess("serviceevent_delete")) {
      if (actionsSet.has("DELETE")) {
        enabledActions.add("DELETE")
      }
    }

    return enabledActions
  }, [serviceEvent, actions, hasAccess])

  const {
    onServiceEventActionClick
  } = useServiceEventAction()

  const _onActionClick = useCallback((action: ServiceEventAction) => {
    if (onActionClick) {
      onActionClick(action)
    } else {
      onServiceEventActionClick(serviceEvent, action)
    }
  }, [onActionClick, onServiceEventActionClick, serviceEvent])

  return (
    <>
      {(showEmpty && visibleActions.size === 0) &&
        <Menu.Item>
          {t("common.action.none", "No Actions")}
        </Menu.Item>
      }
      {visibleActions.has("EDIT") &&
        <Menu.Item icon={<IconEdit size={14}/>} onClick={() => _onActionClick("EDIT")}>
          {t("common.action.edit", "Edit")}
        </Menu.Item>
      }
      {visibleActions.has("DELETE") &&
        <>
          {visibleActions.size > 1 && <Menu.Divider/>}
          <Menu.Item
            onClick={() => _onActionClick("DELETE")}
            icon={<IconTrash size={14}/>}
            color="red">
            {t("common.action.delete", "Delete")}
          </Menu.Item>
        </>
      }
    </>
  )
}
